import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../shared/ui/ag-grid-theme.css'

import type { GridReadyEvent } from 'ag-grid-community'
import { buildTableColDefs, PROCUREMENT_HIDDEN_FIELDS } from './colDefs'
import { useAuthStore } from '../../modules/auth/authStore'
import { useRealtimeStore } from '../../shared/realtime/realtimeStore'
import { useTableGrid } from './useTableGrid'
import { procField, procUserIdFromField, type TableRow } from './types'
import { getTableItems } from '../../shared/api/requestItems.api'
import type { TableFilters } from '../../shared/api/requestItems.api'
import { getProcurementUsers } from '../../shared/api/users.api'
import { getProcurementNotesBulk } from '../../shared/api/procurementNotes.api'
import { Loader } from '../../shared/ui/Loader'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import { TableFiltersBar } from './TableFiltersBar'
import { ResetConfirmDialog } from './ResetConfirmDialog'
import { TableHeaderActions } from './TableHeaderActions'
import { useAgGridScrollRestoration } from '../../shared/hooks/useScrollRestoration'

// Страница при infinite scroll и потолок для «Все строки».
// 10000 — безопасный максимум для autoHeight-рендера AG Grid;
// дальше браузер начинает тормозить, а полноценная Infinite Row Model — отдельной задачей.
const INFINITE_PAGE_SIZE = 100
const ALL_ROWS_CAP = 10000

ModuleRegistry.registerModules([AllCommunityModule])

// Дебаунс — задержка поиска чтобы не слать запрос на каждый символ
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export function TablePage() {
  const gridRef = useRef<AgGridReact<TableRow>>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const role = useAuthStore((s) => s.user?.role ?? '')
  const userId = useAuthStore((s) => s.user?.id ?? '')
  const setGridApi = useRealtimeStore((s) => s.setGridApi)

  // ── Серверные фильтры ──────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [limitVal, setLimitVal] = useState(500)

  const debouncedSearch = useDebounce(searchInput, 400)

  const filters: TableFilters = {
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    clientCategory: categoryFilter || undefined,
    priority: priorityFilter || undefined,
  }

  // limitVal === 0 → «Все строки»: подгружаем пачками INFINITE_PAGE_SIZE,
  // потолок ALL_ROWS_CAP. Иначе — одна страница с фиксированным лимитом.
  const isInfinite = limitVal === 0
  const pageSize = isInfinite ? INFINITE_PAGE_SIZE : limitVal

  const {
    data: pagedData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'table-items',
      debouncedSearch,
      statusFilter,
      categoryFilter,
      priorityFilter,
      pageSize,
      isInfinite,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getTableItems(pageParam, pageSize, filters),
    getNextPageParam: (last, all) => {
      if (!isInfinite) return undefined
      const loaded = all.reduce((n, p) => n + p.rows.length, 0)
      if (loaded >= ALL_ROWS_CAP) return undefined
      if (loaded >= last.total) return undefined
      return all.length + 1
    },
    staleTime: 0,
  })

  const serverData = useMemo(() => {
    const pages = pagedData?.pages ?? []
    if (pages.length === 0) return undefined
    const rows = pages.flatMap((p) => p.rows)
    return { rows, total: pages[0].total, page: 1 }
  }, [pagedData])

  // Загружаем список закупщиков для заголовков колонок
  const { data: procUsers = [] } = useQuery({
    queryKey: ['procurement-users'],
    queryFn: getProcurementUsers,
    staleTime: 60_000,
  })

  // Загружаем заметки закупщиков для текущих строк
  const itemIds = serverData?.rows.map((r) => r.id) ?? []
  const { data: procNotes = [] } = useQuery({
    queryKey: ['procurement-table-notes', itemIds],
    queryFn: () => getProcurementNotesBulk(itemIds),
    enabled: itemIds.length > 0,
    staleTime: 0,
  })

  // Объединяем строки таблицы с заметками закупщиков
  const mergedRows = useMemo(() => {
    const rows = serverData?.rows
    if (!rows) return undefined
    if (!procNotes.length) return rows

    // Индекс заметок: itemId → Map<userId → note>
    const noteIndex = new Map<string, Map<string, string>>()
    for (const n of procNotes) {
      if (!noteIndex.has(n.requestItemId)) noteIndex.set(n.requestItemId, new Map())
      noteIndex.get(n.requestItemId)!.set(n.procurementUserId, n.note ?? '')
    }

    return rows.map((row) => {
      const notes = noteIndex.get(row.id)
      if (!notes) return row
      const patch: Record<string, unknown> = {}
      for (const user of procUsers) {
        const val = notes.get(user.id)
        if (val !== undefined) patch[procField(user.id)] = val
      }
      return Object.keys(patch).length ? { ...row, ...patch } : row
    })
  }, [serverData, procNotes, procUsers])

  // Динамические колонки с именами закупщиков
  const columnDefs = useMemo(() => {
    const allCols = buildTableColDefs(procUsers)

    if (role === 'PROCUREMENT') {
      // Закупщик не видит clientName и чужие колонки-слоты.
      return allCols.filter((col) => {
        const field = col.field as string
        if (PROCUREMENT_HIDDEN_FIELDS.has(field)) return false
        const slotUserId = procUserIdFromField(field)
        if (slotUserId === null) return true
        return slotUserId === userId
      })
    }

    return allCols
  }, [procUsers, role, userId])

  // Map field → procurementUserId (для useEditingEngine)
  const procSlotUserMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const user of procUsers) m.set(procField(user.id), user.id)
    return m
  }, [procUsers])

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)

  const {
    rowData: rawRowData,
    rowSize,
    setRowSize,
    defaultColDef,
    ROW_HEIGHTS,
    onCellFocused,
    onCellMouseDown,
    onCellMouseOver,
    onCellValueChanged,
    addRow,
    processDataFromClipboard,
    onGridReady,
    saveColState,
    resetColState,
    toasts,
  } = useTableGrid(gridRef, containerRef, mergedRows, procSlotUserMap)

  // У закупщика/РОЗ обработанные позиции (заявка уже отправлена клиенту)
  // уезжают в свёрнутый блок «Обработано» под основной таблицей — как в
  // ProcurementQueuePage. У других ролей всё в одном списке.
  const splitProcessed = role === 'PROCUREMENT' || role === 'ROS'
  const activeRowData = useMemo(
    () => (splitProcessed ? rawRowData.filter((r) => !r.isProcessed) : rawRowData),
    [rawRowData, splitProcessed],
  )
  const processedRowData = useMemo(
    () => (splitProcessed ? rawRowData.filter((r) => r.isProcessed) : []),
    [rawRowData, splitProcessed],
  )
  const [processedExpanded, setProcessedExpanded] = useState(false)

  // Вычисляем позицию каждой строки внутри группы заявки (только для активных —
  // в processed-блоке группировка не нужна, он маленький и свёрнут по умолчанию).
  const groupPosMap = useMemo(() => {
    const map = new Map<string, 'first' | 'last' | 'both' | 'middle'>()
    for (let i = 0; i < activeRowData.length; i++) {
      const row = activeRowData[i]
      const key = `${row.code}::${row.requestId}` || row.id
      const prevKey =
        i > 0
          ? `${activeRowData[i - 1].code}::${activeRowData[i - 1].requestId}` ||
            activeRowData[i - 1].id
          : null
      const nextKey =
        i < activeRowData.length - 1
          ? `${activeRowData[i + 1].code}::${activeRowData[i + 1].requestId}` ||
            activeRowData[i + 1].id
          : null
      const isFirst = prevKey !== key
      const isLast = nextKey !== key
      if (isFirst && isLast) map.set(row.id, 'both')
      else if (isFirst) map.set(row.id, 'first')
      else if (isLast) map.set(row.id, 'last')
      else map.set(row.id, 'middle')
    }
    return map
  }, [activeRowData])

  const rowData = activeRowData

  // Восстановление позиции скролла таблицы позиций при возврате назад.
  useAgGridScrollRestoration('table-grid', containerRef)

  const handleGridReady = useCallback(
    (params: GridReadyEvent<TableRow>) => {
      onGridReady()
      setGridApi(params.api)
    },
    [onGridReady, setGridApi],
  )

  // При уходе со страницы обнуляем ссылку на grid api в realtimeStore,
  // иначе WS-событие request_item.updated триггерит getRowNode() на уже
  // destroyed grid → AG Grid warning #26 в консоли.
  useEffect(() => {
    return () => setGridApi(null)
  }, [setGridApi])

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - var(--crm-table-offset, 48px))',
        gap: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', margin: 0 }}>Таблица</h1>
        <TableHeaderActions
          rowSize={rowSize}
          onRowSize={setRowSize}
          onReset={() => setResetConfirmOpen(true)}
          getApi={() => gridRef.current?.api}
          columnDefs={columnDefs}
          onAddRow={addRow}
        />
      </div>

      {/* ── Панель серверных фильтров ── */}
      <TableFiltersBar
        searchInput={searchInput}
        statusFilter={statusFilter}
        categoryFilter={categoryFilter}
        priorityFilter={priorityFilter}
        limitVal={limitVal}
        rowsCount={serverData?.rows.length ?? 0}
        total={serverData?.total}
        onSearchChange={setSearchInput}
        onStatusChange={setStatusFilter}
        onCategoryChange={setCategoryFilter}
        onPriorityChange={setPriorityFilter}
        onLimitChange={setLimitVal}
        onReset={() => {
          setSearchInput('')
          setStatusFilter('')
          setCategoryFilter('')
          setPriorityFilter('')
        }}
      />

      {serverData && serverData.total > serverData.rows.length && (
        <div
          role="alert"
          className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-sm font-medium"
        >
          ⚠ Показано {serverData.rows.length} из {serverData.total} позиций — используйте фильтры
          или увеличьте лимит, чтобы увидеть остальные.
        </div>
      )}

      {isLoading && (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6b7280' }}
        >
          <Loader variant="bus" size={16} />
          Загрузка данных…
        </div>
      )}
      {error && <ErrorMessage error={error} />}
      {isInfinite && (serverData?.rows.length ?? 0) >= ALL_ROWS_CAP && (
        <div
          role="alert"
          className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-sm"
        >
          ⚠ Достигнут потолок {ALL_ROWS_CAP.toLocaleString('ru-RU')} строк. Сузьте фильтры, чтобы
          увидеть оставшиеся.
        </div>
      )}

      <style>{`
        .crm-table .ag-cell { user-select: none; }
        .crm-table .ag-pinned-left-cols-container { box-shadow: 2px 0 4px rgba(0,0,0,0.15); }
      `}</style>
      <div className="crm-table ag-theme-alpine" style={{ flex: 1, minHeight: 0, width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          localeText={AG_GRID_LOCALE_RU}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          suppressDragLeaveHidesColumns
          rowHeight={ROW_HEIGHTS[rowSize]}
          getRowId={(p) => String(p.data.id)}
          getRowClass={(p) => {
            const pos = groupPosMap.get(p.data?.id ?? '')
            return pos ? `req-group-${pos}` : undefined
          }}
          stopEditingWhenCellsLoseFocus
          copyHeadersToClipboard={false}
          enterNavigatesVertically
          enterNavigatesVerticallyAfterEdit
          onGridReady={handleGridReady}
          onColumnPinned={saveColState}
          onColumnMoved={saveColState}
          onColumnResized={saveColState}
          onColumnVisible={saveColState}
          onCellFocused={onCellFocused}
          onCellMouseDown={onCellMouseDown}
          onCellMouseOver={onCellMouseOver}
          onCellValueChanged={onCellValueChanged}
          processDataFromClipboard={processDataFromClipboard}
          onBodyScroll={(e) => {
            if (!isInfinite || !hasNextPage || isFetchingNextPage) return
            const total = e.api.getDisplayedRowCount()
            const last = e.api.getLastDisplayedRowIndex()
            if (total - last < 30) void fetchNextPage()
          }}
        />
      </div>

      {/* Блок «Обработано» для закупщика/РОЗ — позиции из заявок в
          QUOTE_SENT / CLOSED_WON / CLOSED_LOST. По умолчанию свёрнут,
          в основной таблице выше не показываются. */}
      {splitProcessed && processedRowData.length > 0 && (
        <div
          className="rounded-xl border border-gray-200 bg-white shadow-sm"
          style={{ flexShrink: 0 }}
        >
          <button
            type="button"
            onClick={() => setProcessedExpanded((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors rounded-xl"
            aria-expanded={processedExpanded}
          >
            <span className="flex items-center gap-2">
              <svg
                className={`w-4 h-4 transition-transform ${processedExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              Обработано
              <span className="text-gray-400 font-normal">({processedRowData.length})</span>
            </span>
            <span className="text-xs text-gray-400">Заявки с отправленным КП</span>
          </button>
          {processedExpanded && (
            <div
              className="crm-table ag-theme-alpine border-t border-gray-200"
              style={{ height: 'min(420px, 50vh)', minHeight: 240 }}
            >
              <AgGridReact
                localeText={AG_GRID_LOCALE_RU}
                rowData={processedRowData}
                columnDefs={columnDefs}
                defaultColDef={{ ...defaultColDef, editable: false }}
                rowHeight={ROW_HEIGHTS[rowSize]}
                getRowId={(p) => String(p.data.id)}
                suppressDragLeaveHidesColumns
                suppressCellFocus
              />
            </div>
          )}
        </div>
      )}

      {/* Диалог подтверждения сброса вида таблицы */}
      <ResetConfirmDialog
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        onConfirm={() => {
          resetColState()
          setResetConfirmOpen(false)
        }}
      />

      {/* Toast-уведомления об ошибках сохранения */}
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 9999,
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              fontSize: 13,
              color: '#fff',
              background: t.type === 'error' ? '#dc2626' : '#16a34a',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxWidth: 360,
            }}
          >
            {t.text}
          </div>
        ))}
      </div>
    </div>
  )
}
